import { RouteSegment } from 'features/BookingFlow'
import { Container } from 'features/ShipmentDetails'
import { findLastIndex } from 'lodash-es'

import {
  IPdadItem,
  IPdadItemInit,
  PDAD_ARRIVAL,
  PDAD_TSP1,
  PDAD_TSP2,
  PDAD_TSP3,
  PDAD_TSP4,
  PdadStepsSequence,
  TPdadType,
} from '../domain/PdadTypes'
import { TTransportOrCarriageMode } from '../domain/TransportMode'
import { TTransportMode } from '../domain/transportModes'
import { EventCode, IShipTpEvent } from '../domain/TransportPlanEvent'
import { TTransportType } from '../domain/TransportType'
import { tpMileStoneUI } from '../view/TPPartials'
import { getPdadEvents, getPdadMilestones, PdadStepUI, TPdadRecord } from './PDAD'
import { IMinimalContainerJSON } from './PdadUtils'
import {
  ICargoProcess,
  ICarriagePlan,
  IMilestoneDelivery,
  IMilestonePickup,
  ITransportPlan2,
  TpPoint,
  Trackable,
} from './TransportPlan2'
import { getAllEvents, getDurationInCalendarDays } from './transportPlanEventUtils'
import { getAllMilestones } from './transportPlanMilestoneUtils'

const {
  arrivalPodUI,
  arrivalRailUI,
  cargoReadyUI,
  cgomdUI,
  cgomoUI,
  deliveryUI,
  departureAirportUI,
  departurePolUI,
  departureRailUI,
  dprUI,
  destinationAirportUI,
  pickupUI,
  transshipmentUI,
} = tpMileStoneUI

/*
Map for the Milestones per Step
For certain cases to show initialProgress which is not calculated from timestamps.
*/
const InitialProgressMap = {
  lcl: {
    air: {
      [PDAD_ARRIVAL]: EventCode.ADO,
    },
  },
}

export interface IContainerTP {
  transportType: TTransportType
  transportMode: TTransportOrCarriageMode
  containerIndex: number
  origin: ICargoProcess
  pre: ICarriagePlan
  main: ICarriagePlan
  on: ICarriagePlan
  events: Record<string, IShipTpEvent>
  lastActualIndex: number
  pdadEvents: Record<TPdadType, IShipTpEvent | undefined>
  pdadSequence: IPdadItem[]
  lastActualPdad: IPdadItem
  pdadLength: number
  groupNumbers?: number[] // container numbers (index + 1)
}

export const getContainersPlans = (
  tp: ITransportPlan2,
  containers: (IMinimalContainerJSON | Container)[]
): IContainerTP[] => {
  const nowMs: number = new Date().getTime()
  const containersPdad = containers.map((container, containerIndex) => {
    return getContainerTp(tp, container, containerIndex, nowMs)
  })
  return containersPdad
}

type GetPdadItem = (props: {
  pdadsInit: IPdadItemInit[]
  pidx: number
  lastActualIndex: number
  nowMs: number
  main: ICarriagePlan
  initialProgress: number
}) => IPdadItem

// @ts-expect-error TS(2322) FIXME: Type '({ pdadsInit, pidx, lastActualIndex, nowMs, ... Remove this comment to see the full error message
const getPdadItem: GetPdadItem = ({ pdadsInit, pidx, lastActualIndex, nowMs, main, initialProgress }) => {
  const mainSegments: RouteSegment[] = main.route.segments
  const pdadInitItem: IPdadItemInit = pdadsInit[pidx]

  const passedActual = lastActualIndex >= pidx
  const allowProgress = lastActualIndex >= pidx - 1

  const isLastActual = lastActualIndex === pidx

  let isProgress = !!initialProgress
  let progress = initialProgress
  const transporter = getMainTransporter(main, pdadInitItem.pdadType)
  const durationDays = getDurationDays(mainSegments, pdadInitItem.pdadType, main.events)
  const prevPdadItem = pdadsInit[pidx - 1]
  if (
    !passedActual &&
    allowProgress &&
    pdadInitItem.event.aggregatedTimestamp &&
    prevPdadItem?.event?.aggregatedTimestamp?.date
  ) {
    const aggregatedMs = new Date(pdadInitItem.event.aggregatedTimestamp.date).getTime()
    const prevAggregatedMs = new Date(prevPdadItem.event.aggregatedTimestamp.date).getTime()
    isProgress = nowMs > prevAggregatedMs
    if (isProgress) {
      const realProgress = (100 * (nowMs - prevAggregatedMs)) / (aggregatedMs - prevAggregatedMs)
      progress = Math.max(Math.min(90, realProgress), 0)
    }
  }

  const allowShowOnStep =
    !isProgress &&
    (isLastActual || // any step
      ((!prevPdadItem || !prevPdadItem.event) && !passedActual)) // first step

  return {
    passedActual,
    allowShowOnStep,
    progress,
    isProgress,
    durationDays,
    transporter,
    ...pdadInitItem,
  }
}

const matchesTrackableId = (trackableId: string) => (trackable: Trackable) => trackable.trackableId === trackableId

export const getCarriagePlanForContainer = <T extends ICarriagePlan | ICargoProcess>(
  carriagePlans: T[],
  containerId: string
): T | undefined => {
  if (!carriagePlans) {
    return undefined
  }

  const matchContainerId = matchesTrackableId(containerId)
  return carriagePlans.find((carriagePlan) => carriagePlan.trackables.some(matchContainerId))
}

export const getTrackable = <T extends ICarriagePlan | ICargoProcess>(
  carriagePlans: T[],
  containerId: string
): Trackable | undefined => {
  if (!carriagePlans) {
    return undefined
  }

  const matchContainerId = matchesTrackableId(containerId)
  const carriagePlanWithContainer = getCarriagePlanForContainer(carriagePlans, containerId)
  return carriagePlanWithContainer?.trackables?.find(matchContainerId)
}

const getContainerTp = (
  tp: ITransportPlan2,
  container: IMinimalContainerJSON | Container | null,
  containerIndex: number,
  nowMs: number
): IContainerTP => {
  const { transportType, transportMode } = tp
  // @ts-expect-error TS(2345) FIXME: Argument of type 'ICargoProcess[] | undefined' is ... Remove this comment to see the full error message
  const origin = container ? getCarriagePlanForContainer(tp.origin, container._id) : tp.origin && tp.origin[0]
  // @ts-expect-error TS(2345) FIXME: Argument of type 'ICarriagePlan[] | undefined' is ... Remove this comment to see the full error message
  const pre = container ? getCarriagePlanForContainer(tp.pre, container._id) : tp.pre && tp.pre[0]
  // @ts-expect-error TS(2345) FIXME: Argument of type 'ICarriagePlan[] | undefined' is ... Remove this comment to see the full error message
  const main = container ? getCarriagePlanForContainer(tp.main, container._id) : tp.main && tp.main[0]
  // @ts-expect-error TS(2345) FIXME: Argument of type 'ICarriagePlan[] | undefined' is ... Remove this comment to see the full error message
  const on = container ? getCarriagePlanForContainer(tp.on, container._id) : tp.on && tp.on[0]

  // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan | undefined' is not assignable... Remove this comment to see the full error message
  const milestones = getAllMilestones([pre, main, on])
  // @ts-expect-error TS(2345) FIXME: Argument of type 'ICarriagePlan | undefined' is no... Remove this comment to see the full error message
  const pdadMilestones = getPdadMilestones(milestones, main)
  // @ts-expect-error TS(2322) FIXME: Type 'ICargoProcess | undefined' is not assignable... Remove this comment to see the full error message
  const events = getAllEvents([origin, pre, main, on])

  if (pdadMilestones.arrival) {
    // workaround if segment doesn't have duration
    // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
    if (main.route.segments && main.route.segments.length === 1 && !main.route.segments[0].duration) {
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      main.route.segments[0].duration = main.route.duration
    }
  }

  const pdadEvents = getPdadEvents(pdadMilestones, events, transportType, transportMode)

  // @ts-expect-error TS(2322) FIXME: Type '({ pdadType: TPdadType; event: IShipTpEvent ... Remove this comment to see the full error message
  const pdadsInit: IPdadItemInit[] = PdadStepsSequence.map((step) =>
    pdadEvents[step]
      ? {
          pdadType: step as TPdadType,
          event: pdadEvents[step],
        }
      : null
  ).filter((item) => item)
  const pdadLength = pdadsInit.length
  // @ts-expect-error TS(2345) FIXME: Argument of type '(pdad: IPdadItemInit) => boolean... Remove this comment to see the full error message
  const lastActualIndex = findLastIndex(pdadsInit, (pdad) => pdad.event.actual)

  const pdadSequence: IPdadItem[] = pdadsInit.map((item, i) =>
    getPdadItem({
      pdadsInit,
      pidx: i,
      lastActualIndex,
      nowMs,
      // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan | undefined' is not assignable... Remove this comment to see the full error message
      main,
      initialProgress: events[InitialProgressMap[transportType]?.[transportMode]?.[item.pdadType]]?.actual ? 3 : 0,
    })
  )

  const lastActualPdad = pdadSequence[lastActualIndex]

  return {
    transportType,
    transportMode,
    containerIndex,
    // @ts-expect-error TS(2322) FIXME: Type 'ICargoProcess | undefined' is not assignable... Remove this comment to see the full error message
    origin,
    // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan | undefined' is not assignable... Remove this comment to see the full error message
    pre,
    // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan | undefined' is not assignable... Remove this comment to see the full error message
    main,
    // @ts-expect-error TS(2322) FIXME: Type 'ICarriagePlan | undefined' is not assignable... Remove this comment to see the full error message
    on,
    events,
    lastActualPdad,
    lastActualIndex,
    pdadEvents,
    pdadSequence,
    pdadLength,
  }
}

export const getPdadStructure = (containerTp: IContainerTP): TPdadRecord => {
  const { pre, main, on, transportMode } = containerTp
  const milestones = getAllMilestones([pre, main, on])
  const pdadMilestones = getPdadMilestones(milestones, main)

  const getDepartureUI = (): PdadStepUI => {
    if (milestones.pol) {
      return departurePolUI
    }
    if (milestones.departureAirport) {
      return departureAirportUI
    }
    if (milestones.departure && transportMode === 'rail') {
      return departureRailUI
    }
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'PdadStepUI'... Remove this comment to see the full error message
    return null
  }

  const getArrivalUI = (): PdadStepUI => {
    if (milestones.pod) {
      return arrivalPodUI
    }
    if (milestones.destinationAirport) {
      return destinationAirportUI
    }
    if (milestones.arrival && transportMode === 'rail') {
      return arrivalRailUI
    }
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'PdadStepUI'... Remove this comment to see the full error message
    return null
  }

  const getArrivalTransportMode = (): TTransportMode => {
    if (milestones.pod) {
      return 'sea'
    }
    if (milestones.destinationAirport) {
      return 'air'
    }

    if (milestones.arrival && transportMode === 'rail') {
      return 'rail'
    }

    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'TTransportM... Remove this comment to see the full error message
    return null
  }

  // there could be others segments like from and to depot
  // so use last segment for pre-carriage, first segment for on-carriage

  const departureUI = getDepartureUI()

  const arrivalUI = getArrivalUI()

  const arrivalTransportMode = getArrivalTransportMode()

  const isMultimodalOnCarriage = on ? on.route.transportMode === 'rail' || on.route.transportMode === 'barge' : false

  const pdad2: TPdadRecord = {
    cargoReady: {
      stepUI: cargoReadyUI,
    },
    cgomd: {
      stepUI: cgomdUI,
      transportMode: on ? on.route.transportMode : null,
    },
    cgomo: {
      stepUI: cgomoUI,
      // From pickup to CGOMO we always have truck
      transportMode: 'truck',
    },
    pickup: pdadMilestones.pickup && {
      stepUI: pickupUI,
      milestone: pdadMilestones.pickup,
      point: pdadMilestones.pickup && (pdadMilestones.pickup as IMilestonePickup).address,
    },
    dpr: pdadMilestones.dpr && {
      stepUI: dprUI,
      milestone: pdadMilestones.dpr.port,
      point: {
        name: pdadMilestones.dpr.port?.name,
      } as TpPoint,
      transportMode: pre?.route.transportMode ?? null,
    },
    departure: pdadMilestones.departure && {
      stepUI: departureUI,
      milestone: pdadMilestones.departure,
      point: main.route.journey.origin,
      transportMode: pre ? pre.route.transportMode : null,
    },
    arrival: pdadMilestones.arrival && {
      stepUI: arrivalUI,
      milestone: pdadMilestones.arrival,
      point: main.route.journey.destination,
      transportMode: arrivalTransportMode,
    },
    delivery: pdadMilestones.delivery && {
      stepUI: deliveryUI,
      milestone: pdadMilestones.delivery,
      point: pdadMilestones.delivery && (pdadMilestones.delivery as IMilestoneDelivery).address,
      transportMode: isMultimodalOnCarriage ? 'truck' : on ? on.route.transportMode : null,
    },
    tsp1: pdadMilestones.tsp1 && {
      stepUI: transshipmentUI,
      milestone: main.milestones.pol,
      point: pdadMilestones.tsp1 as TpPoint,
      transportMode: 'sea',
    },
    tsp2: pdadMilestones.tsp2 && {
      stepUI: transshipmentUI,
      milestone: main.milestones.pol,
      point: pdadMilestones.tsp2 as TpPoint,
      transportMode: 'sea',
    },
    tsp3: pdadMilestones.tsp3 && {
      stepUI: transshipmentUI,
      milestone: main.milestones.pol,
      point: pdadMilestones.tsp3 as TpPoint,
      transportMode: 'sea',
    },
    tsp4: pdadMilestones.tsp4 && {
      stepUI: transshipmentUI,
      milestone: main.milestones.pol,
      point: pdadMilestones.tsp4 as TpPoint,
      transportMode: 'sea',
    },
  }
  return pdad2
}

const getDurationDays = (
  mainSegments: RouteSegment[],
  pdadType: TPdadType,
  events: ICarriagePlan['events']
): number | null => {
  let durationDays = null
  const { vdl, vdt1, vdt2, vdt3, vdt4 } = events
  if (pdadType === PDAD_ARRIVAL && mainSegments) {
    const legsLength = mainSegments.length
    // @ts-expect-error TS(2322) FIXME: Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
    durationDays = mainSegments[legsLength - 1] && mainSegments[legsLength - 1].durationInCalendarDays
  }
  if (pdadType === PDAD_TSP1) {
    // @ts-expect-error TS(2322) FIXME: Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
    durationDays = getDurationInCalendarDays(vdl, vdt1)
  }
  if (pdadType === PDAD_TSP2) {
    // @ts-expect-error TS(2322) FIXME: Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
    durationDays = getDurationInCalendarDays(vdt1, vdt2)
  }
  if (pdadType === PDAD_TSP3) {
    // @ts-expect-error TS(2322) FIXME: Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
    durationDays = getDurationInCalendarDays(vdt2, vdt3)
  }
  if (pdadType === PDAD_TSP4) {
    // @ts-expect-error TS(2322) FIXME: Type 'number | null' is not assignable to type 'nu... Remove this comment to see the full error message
    durationDays = getDurationInCalendarDays(vdt3, vdt4)
  }
  return durationDays
}

const getMainTransporter = (carriagePlan: ICarriagePlan, pdadType: TPdadType): string => {
  if (pdadType === PDAD_ARRIVAL) {
    if (carriagePlan.route.flightNumber) {
      return carriagePlan.route.flightNumber
    }
    if (carriagePlan.milestones.departureAirport) {
      // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
      return carriagePlan.milestones.departureAirport.flightNumber
    }
  }
  if (carriagePlan.route.routeSchedule) {
    return getMainVessel(carriagePlan, pdadType)
  }
  // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'string'.
  return null
}

const getMainVessel = (main: ICarriagePlan, pdadType: TPdadType): string => {
  const legs = main.route.routeSchedule && main.route.routeSchedule.oceanLegs
  if (!legs) {
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'string'.
    return null
  }
  const legIndex = getLegIndexForPdadType(pdadType, legs.length)
  if (legIndex === -1) {
    // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'string'.
    return null
  }
  return legs[legIndex].vessel && legs[legIndex].vessel.name
}

const getLegIndexForPdadType = (pdadType: TPdadType, legsLength: number): number => {
  switch (pdadType) {
    case PDAD_TSP1:
      return 0
    case PDAD_TSP2:
      return 1
    case PDAD_TSP3:
      return 2
    case PDAD_TSP4:
      return 3
    case PDAD_ARRIVAL:
      return legsLength - 1
    default:
      return -1
  }
}

export const getContainersTpSortedByActual = (containersTp: IContainerTP[]) => {
  // furthest is first in array
  return [...containersTp].sort((a, b) => {
    if (b.lastActualIndex > a.lastActualIndex) {
      return 1
    }
    if (a.lastActualIndex > b.lastActualIndex) {
      return -1
    }
    if (
      a.lastActualPdad &&
      a.lastActualPdad.event &&
      a.lastActualPdad.event.aggregatedTimestamp &&
      b.lastActualPdad &&
      b.lastActualPdad.event &&
      b.lastActualPdad.event.aggregatedTimestamp
    ) {
      return a.lastActualPdad.event.aggregatedTimestamp.date < b.lastActualPdad.event.aggregatedTimestamp.date ? 1 : -1
    }
    return 0
  })
}

const groupRange = 1000 * 60 * 60

const pushToContainersGroup = (groups: IContainerTP[], containerTp: IContainerTP) => {
  // mutate container plan
  containerTp.groupNumbers = [containerTp.containerIndex + 1]
  groups.push(containerTp)
}

export const getContainersTpGroups = (containersTp: IContainerTP[]) => {
  return containersTp.reduce((groups, containerTp, i) => {
    if (i === 0) {
      pushToContainersGroup(groups, containerTp)
      return groups
    }

    const prevInGroup = groups[groups.length - 1] as IContainerTP
    const prevStamp =
      prevInGroup.lastActualPdad &&
      prevInGroup.lastActualPdad.event &&
      prevInGroup.lastActualPdad.event.aggregatedTimestamp

    if (prevInGroup.lastActualIndex === containerTp.lastActualIndex) {
      if (prevInGroup.lastActualIndex === containerTp.pdadLength - 1) {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        prevInGroup.groupNumbers.push(containerTp.containerIndex + 1)
        return groups
      }
      if (
        prevInGroup.lastActualPdad &&
        prevInGroup.lastActualPdad.passedActual &&
        !prevInGroup.lastActualPdad.isProgress &&
        containerTp.lastActualPdad &&
        containerTp.lastActualPdad.passedActual &&
        !containerTp.lastActualPdad.isProgress
      ) {
        // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        prevInGroup.groupNumbers.push(containerTp.containerIndex + 1)
        return groups
      }

      const stamp =
        containerTp.lastActualPdad &&
        containerTp.lastActualPdad.event &&
        containerTp.lastActualPdad.event.aggregatedTimestamp
      const ms = stamp && new Date(stamp.date).getTime()
      const prevMs = prevStamp && new Date(prevStamp.date).getTime()
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      if (stamp && prevStamp && Math.abs(prevMs - ms) > groupRange) {
        pushToContainersGroup(groups, containerTp)
        return groups
      }
      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      prevInGroup.groupNumbers.push(containerTp.containerIndex + 1)
      return groups
    }
    pushToContainersGroup(groups, containerTp)
    return groups
  }, [])
}
